<template>
  <!-- The admin drawer's "make them a …" buttons: one per other type, each
       with its type's identity color on the icon -->
  <div class="flex flex-wrap gap-2">
    <Button
      v-for="target in targets"
      :key="target"
      :loading="loading"
      outlined
      severity="secondary"
      size="small"
      @click="confirmTypeChange(target)"
    >
      <Icon
        v-if="target === 'redshirt'"
        name="simple-icons:startrek"
        class="mr-1 !text-sm !leading-none text-[color:var(--kb-type-redshirt)]"
      />
      <i
        v-else-if="target === 'captain'"
        class="pi pi-star-fill mr-1 !text-xs text-[color:var(--kb-type-captain)]"
      />
      <i
        v-else
        class="pi pi-user mr-1 !text-xs text-[color:var(--kb-type-member)]"
      />
      {{ LABELS[target] }}
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Member } from '~/shared/types/member'
import type { MemberTypeValue } from '~/shared/types/memberType.js'

type TypeChoice = 'member' | 'redshirt' | 'captain'

const TYPES: TypeChoice[] = ['member', 'redshirt', 'captain']
const LABELS: Record<TypeChoice, string> = {
  member: 'Make member',
  redshirt: 'Make red shirt',
  captain: 'Make captain',
}

const props = defineProps<{
  member: Member | Partial<Member>
  loading?: boolean
}>()

const emit = defineEmits<{
  confirmTypeChange: [change: { currentType: MemberTypeValue, newType: TypeChoice, hasSubkrewe: boolean }]
}>()

const currentMemberType = computed<MemberTypeValue>(() => props.member.member_type || 'member')

// The other two types; none for a type without actions (pending)
const targets = computed(() => TYPES.includes(currentMemberType.value as TypeChoice)
  ? TYPES.filter(type => type !== currentMemberType.value)
  : [])

// The parent opens the confirmation dialog
function confirmTypeChange(newType: TypeChoice) {
  emit('confirmTypeChange', {
    currentType: currentMemberType.value,
    newType,
    hasSubkrewe: !!props.member.subkrewe,
  })
}
</script>
